import React, { useCallback, useEffect, useRef, useState } from "react";
import { createGame, tick, movePaddle, getAIMove } from "./engine.js";
import type { AirHockeyGame, Level } from "./types.js";
import { CANVAS_W, CANVAS_H, WALL_PADDING, GOAL_SIZE } from "./types.js";
import ConfettiSplash from "../../lib/ConfettiSplash.js";

type Mode = "cpu" | "local" | "multiplayer";
type PlayerColor = "red" | "yellow" | "green" | "purple" | "orange" | "teal";

const COLOR_OPTIONS: PlayerColor[] = ["red", "yellow", "green", "purple", "orange", "teal"];

const CODE_WORDS = [
  "apple", "beach", "candy", "dance", "eagle", "flame", "grape", "honey",
  "ivory", "jelly", "koala", "lemon", "mango", "noble", "ocean", "pearl",
  "queen", "rain", "sugar", "tiger", "umbra", "vivid", "whale", "xenon",
  "yacht", "zebra", "bloom", "coral", "dawn", "ember", "frost", "glow",
  "haven", "iris", "jade", "kite", "lune", "mist", "nova", "opal",
];

function generateCode(): string {
  const pick = () => CODE_WORDS[Math.floor(Math.random() * CODE_WORDS.length)];
  return `${pick()}-${pick()}-${pick()}`;
}

export default function AirHockeyPage({ hideTitle = false }: { hideTitle?: boolean }) {
  const [mode, setMode] = useState<Mode>("cpu");
  const [level, setLevel] = useState<Level>("medium");
  const [game, setGame] = useState<AirHockeyGame>(() => createGame("medium"));
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const gameRef = useRef(game);
  const lastTimeRef = useRef(0);
  const rafRef = useRef(0);
  const p1Keys = useRef({ up: false, down: false });
  const p2Keys = useRef({ up: false, down: false });

  const [mpConnected, setMpConnected] = useState(false);
  const [mpPlayerId, setMpPlayerId] = useState<1 | 2 | null>(null);
  const [roomCode, setRoomCode] = useState("");
  const [mpToken, setMpToken] = useState("");
  const [opponentName, setOpponentName] = useState("");
  const [yourTurn, setYourTurn] = useState(false);
  const [mpError, setMpError] = useState("");
  const [joinCode, setJoinCode] = useState(generateCode);
  const [p1Name, setP1Name] = useState("Player 1");
  const [p1Color, setP1Color] = useState<PlayerColor>("red");

  const pollRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollingRef = useRef(false);
  const roomCodeRef = useRef(roomCode);
  const mpPlayerIdRef = useRef(mpPlayerId);
  const mpConnectedRef = useRef(mpConnected);

  roomCodeRef.current = roomCode;
  mpPlayerIdRef.current = mpPlayerId;
  mpConnectedRef.current = mpConnected;
  gameRef.current = game;

  const opponentInputRef = useRef({ wantsUp: false, wantsDown: false });

  function resetGame() {
    if (mode === "multiplayer") {
      apiPost("/api/airhockey/reset", { code: roomCode, playerId: mpPlayerId, token: mpToken });
      return;
    }
    setGame(createGame(level));
  }

  function handleLevelChange(next: Level) {
    setLevel(next);
    setGame(createGame(next));
  }

  function handleModeChange(next: Mode) {
    if (mode === "multiplayer") {
      if (roomCode && mpToken) apiPost("/api/leave", { code: roomCode, playerId: mpPlayerId, token: mpToken });
      stopPolling();
    }
    setMode(next);
    setGame(createGame(level));
  }

  function render(ctx: CanvasRenderingContext2D, g: AirHockeyGame) {
    ctx.clearRect(0, 0, CANVAS_W, CANVAS_H);
    ctx.fillStyle = "#0a1a2a";
    ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);
    ctx.strokeStyle = "rgba(0, 180, 255, 0.1)";
    ctx.lineWidth = 2;
    ctx.strokeRect(WALL_PADDING, WALL_PADDING, CANVAS_W - WALL_PADDING * 2, CANVAS_H - WALL_PADDING * 2);
    ctx.strokeStyle = "rgba(255, 255, 255, 0.08)";
    ctx.lineWidth = 1;
    ctx.setLineDash([6, 8]);
    ctx.beginPath();
    ctx.moveTo(CANVAS_W / 2, WALL_PADDING);
    ctx.lineTo(CANVAS_W / 2, CANVAS_H - WALL_PADDING);
    ctx.stroke();
    ctx.setLineDash([]);
    const goalY = (CANVAS_H - GOAL_SIZE) / 2;
    ctx.fillStyle = "rgba(0, 180, 255, 0.06)";
    ctx.fillRect(0, goalY, WALL_PADDING, GOAL_SIZE);
    ctx.fillRect(CANVAS_W - WALL_PADDING, goalY, WALL_PADDING, GOAL_SIZE);
    ctx.strokeStyle = "rgba(0, 180, 255, 0.3)";
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.moveTo(WALL_PADDING, goalY); ctx.lineTo(0, goalY);
    ctx.moveTo(WALL_PADDING, goalY + GOAL_SIZE); ctx.lineTo(0, goalY + GOAL_SIZE);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(CANVAS_W - WALL_PADDING, goalY); ctx.lineTo(CANVAS_W, goalY);
    ctx.moveTo(CANVAS_W - WALL_PADDING, goalY + GOAL_SIZE); ctx.lineTo(CANVAS_W, goalY + GOAL_SIZE);
    ctx.stroke();
    ctx.fillStyle = "#3498db";
    ctx.shadowBlur = 20;
    ctx.shadowColor = "#3498db66";
    ctx.beginPath();
    ctx.arc(g.p1.x, g.p1.y, g.p1.radius, 0, Math.PI * 2);
    ctx.fill();
    ctx.shadowBlur = 0;
    ctx.fillStyle = "#e74c3c";
    ctx.shadowBlur = 20;
    ctx.shadowColor = "#e74c3c66";
    ctx.beginPath();
    ctx.arc(g.p2.x, g.p2.y, g.p2.radius, 0, Math.PI * 2);
    ctx.fill();
    ctx.shadowBlur = 0;
    ctx.fillStyle = "#fff";
    ctx.shadowBlur = 24;
    ctx.shadowColor = "#ffffff88";
    ctx.beginPath();
    ctx.arc(g.puck.x, g.puck.y, g.puck.radius, 0, Math.PI * 2);
    ctx.fill();
    ctx.shadowBlur = 0;
    ctx.fillStyle = "#8ab4d0";
    ctx.font = "bold 32px monospace";
    ctx.textAlign = "center";
    ctx.textBaseline = "top";
    ctx.fillText(`${g.score[0]}`, CANVAS_W * 0.35, 16);
    ctx.fillText(`${g.score[1]}`, CANVAS_W * 0.65, 16);
  }

  useEffect(() => {
    if (!canvasRef.current) return;
    const ctx = canvasRef.current.getContext("2d");
    if (!ctx) return;
    render(ctx, game);
  }, [game]);

  useEffect(() => {
    lastTimeRef.current = performance.now();
    let aiAccum = 0;
    function loop(time: number) {
      const dt = time - lastTimeRef.current;
      lastTimeRef.current = time;
      if (dt < 200) setGame((prev) => tick(prev, dt));
      const g = gameRef.current;
      if (!g.winner) {
        if (mode === "local") {
          const pk2 = p2Keys.current;
          if (pk2.up) setGame((prev) => movePaddle(prev, 2, -1));
          if (pk2.down) setGame((prev) => movePaddle(prev, 2, 1));
        }
        if (mode === "multiplayer") {
          const opp = opponentInputRef.current;
          if (opp.wantsUp) setGame((prev) => movePaddle(prev, 2, -1));
          if (opp.wantsDown) setGame((prev) => movePaddle(prev, 2, 1));
        }
        if (mode === "cpu") {
          aiAccum += dt;
          if (aiAccum > 20) {
            aiAccum = 0;
            setGame((prev) => {
              if (prev.winner) return prev;
              return movePaddle(prev, 2, getAIMove(prev));
            });
          }
        }
        const pk1 = p1Keys.current;
        if (pk1.up) setGame((prev) => movePaddle(prev, 1, -1));
        if (pk1.down) setGame((prev) => movePaddle(prev, 1, 1));
      }
      rafRef.current = requestAnimationFrame(loop);
    }
    rafRef.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(rafRef.current);
  }, [mode]);

  // ---- input relay for multiplayer ----------------------------------------

  useEffect(() => {
    if (mode !== "multiplayer" || !mpConnected) return;
    const interval = setInterval(async () => {
      const g = gameRef.current;
      if (g.winner) return;
      const res = await apiPost("/api/airhockey/input", {
        code: roomCode, playerId: mpPlayerId, token: mpToken,
        wantsUp: p1Keys.current.up,
        wantsDown: p1Keys.current.down,
      });
      if (res.opponentInput) opponentInputRef.current = res.opponentInput;
    }, 80);
    return () => clearInterval(interval);
  }, [mode, mpConnected, roomCode, mpPlayerId, mpToken]);

  // ---- keyboard -----------------------------------------------------------

  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (e.key === "w" || e.key === "W") p1Keys.current.up = true;
      if (e.key === "s" || e.key === "S") p1Keys.current.down = true;
      if (e.key === "ArrowUp") { e.preventDefault(); p2Keys.current.up = true; }
      if (e.key === "ArrowDown") { e.preventDefault(); p2Keys.current.down = true; }
    }
    function handleKeyUp(e: KeyboardEvent) {
      if (e.key === "w" || e.key === "W") p1Keys.current.up = false;
      if (e.key === "s" || e.key === "S") p1Keys.current.down = false;
      if (e.key === "ArrowUp") p2Keys.current.up = false;
      if (e.key === "ArrowDown") p2Keys.current.down = false;
    }
    window.addEventListener("keydown", handleKey);
    window.addEventListener("keyup", handleKeyUp);
    return () => { window.removeEventListener("keydown", handleKey); window.removeEventListener("keyup", handleKeyUp); };
  }, []);

  // ---- API / server event helpers -----------------------------------------

  async function apiPost(path: string, body: object) {
    try {
      const res = await fetch(path, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
      return await res.json();
    } catch { return { type: "error", message: "Network error." }; }
  }

  function handleServerEvent(event: any) {
    switch (event.type) {
      case "opponent-joined":
        setOpponentName(event.playerName);
        setMpConnected(true);
        setMessage(`Opponent ${event.playerName} joined! Press New Game to start.`);
        break;
      case "game-reset":
        setGame(createGame(level));
        break;
      case "opponent-left":
        setMpConnected(false);
        setRoomCode("");
        setOpponentName("");
        setMpPlayerId(null);
        setMpToken("");
        setMessage("Opponent left the game.");
        setGame(createGame(level));
        break;
      case "error":
        setMpError(event.message);
        break;
    }
  }

  const startPolling = useCallback(() => {
    if (pollingRef.current) return;
    pollingRef.current = true;
    async function poll() {
      if (!pollingRef.current) return;
      const code = roomCodeRef.current;
      const pid = mpPlayerIdRef.current;
      if (!code || !pid) { pollingRef.current = false; return; }
      try {
        const res = await fetch(`/api/poll?code=${encodeURIComponent(code)}&playerId=${pid}`);
        const data = await res.json();
        for (const event of data.events) { handleServerEvent(event); if (event.type === "opponent-left") { pollingRef.current = false; return; } }
      } catch { /* retry */ }
      if (pollingRef.current) pollRef.current = setTimeout(poll, 200);
    }
    poll();
  }, []);

  function stopPolling() {
    pollingRef.current = false;
    if (pollRef.current) { clearTimeout(pollRef.current); pollRef.current = null; }
  }

  useEffect(() => { return () => stopPolling(); }, []);

  // ---- multiplayer room actions -------------------------------------------

  const [message, setMessage] = useState("");

  async function handleCreateRoom() {
    const result = await apiPost("/api/create-room", { playerName: p1Name.trim() || "Player 1", playerColor: p1Color });
    if (result.type === "error") { setMpError(result.message); return; }
    roomCodeRef.current = result.code;
    mpPlayerIdRef.current = result.playerId;
    setRoomCode(result.code);
    setMpPlayerId(result.playerId);
    setMpToken(result.token);
    setMessage(`Room created! Share this code: ${result.code}`);
    startPolling();
  }

  async function handleJoinRoom() {
    if (!joinCode.trim()) { setMpError("Enter a room code."); return; }
    const result = await apiPost("/api/join-room", { code: joinCode.trim(), playerName: p1Name.trim() || "Player 2", playerColor: p1Color });
    if (result.type === "error") { setMpError(result.message); return; }
    roomCodeRef.current = joinCode.trim();
    mpPlayerIdRef.current = result.playerId;
    setMpPlayerId(result.playerId);
    setMpToken(result.token);
    setRoomCode(joinCode.trim());
    setOpponentName(result.opponentName);
    setP1Color(result.opponentColor);
    setMpConnected(true);
    setMessage(`Connected with ${result.opponentName}. Press New Game to start!`);
    startPolling();
  }

  async function handleLeaveGame() {
    await apiPost("/api/leave", { code: roomCode, playerId: mpPlayerId, token: mpToken });
    stopPolling();
    setMpConnected(false);
    setRoomCode("");
    setOpponentName("");
    setMpPlayerId(null);
    setMpToken("");
    setGame(createGame(level));
    setMessage("Left the game.");
  }

  return (
    <div className="game-shell">
      <ConfettiSplash active={game.winner !== null} winnerName={game.winner === 1 ? "Player 1" : game.winner === 2 ? "Player 2" : undefined} />
      {!hideTitle && <h1 className="title">Air Hockey</h1>}

      <section className="control-panel">
        <p className="status">{game.winner ? `Player ${game.winner} wins!` : game.message}</p>
        <div className="row two-col">
          <label>Mode <select value={mode} onChange={(e) => handleModeChange(e.target.value as Mode)}>
            <option value="cpu">vs Computer</option>
            <option value="local">Two Players Local</option>
            <option value="multiplayer">Multiplayer Online</option>
          </select></label>
          <label>Level <select value={level} onChange={(e) => handleLevelChange(e.target.value as Level)}>
            <option value="easy">Easy</option>
            <option value="medium">Medium</option>
            <option value="hard">Hard</option>
          </select></label>
        </div>

        {mode === "multiplayer" && !mpConnected && (
          <div className="row two-col">
            <label>Your Name <input value={p1Name} onChange={(e) => setP1Name(e.target.value)} maxLength={20} /></label>
            <label>Your Color <select value={p1Color} onChange={(e) => setP1Color(e.target.value as PlayerColor)}>{COLOR_OPTIONS.map((c) => (<option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>))}</select></label>
          </div>
        )}

        {mode === "multiplayer" && !mpConnected && !roomCode && (
          <div className="row two-col"><label>Room Code <input value={joinCode} onChange={(e) => setJoinCode(e.target.value)} placeholder="e.g. apple-blue-rain" maxLength={40} /></label></div>
        )}

        {mode === "multiplayer" && !mpConnected && !roomCode && (
          <div className="mp-actions" style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
            <button className="mp-btn" onClick={handleCreateRoom}>Create Room</button>
            <button className="mp-btn mp-btn-secondary" onClick={handleJoinRoom}>Join Room</button>
          </div>
        )}

        {mode === "multiplayer" && roomCode && !mpConnected && (
          <div style={{ textAlign: 'center' }}>
            <p className="mp-label">Share this code with a friend:</p>
            <p className="mp-code">{roomCode}</p>
            <p className="mp-waiting">Waiting for opponent to join...</p>
            <button className="mp-cancel" onClick={handleLeaveGame}>Cancel</button>
          </div>
        )}

        {mode === "multiplayer" && mpConnected && (
          <div className="mp-connected-bar"><span className="mp-connected-info">Connected with <strong>{opponentName}</strong></span></div>
        )}

        <div className="actions">
          {(mode !== "multiplayer" || mpConnected) && <button onClick={resetGame}>New Game</button>}
          {mode === "multiplayer" && mpConnected && <button className="mp-leave" onClick={handleLeaveGame}>Leave Game</button>}
        </div>

        {mpError && <p className="subtle mp-error">{mpError}</p>}
        {message && mode !== "multiplayer" && <p className="subtle">{message}</p>}
      </section>

      <section className="board-panel">
        <canvas ref={canvasRef} width={CANVAS_W} height={CANVAS_H} className="airhockey-canvas" />
        <div className="how-to-play">
          <p className="how-title">Controls</p>
          {mode === "local" ? (
            <><p>P1: W/S to move paddle</p><p>P2: ↑/↓ to move paddle</p></>
          ) : (
            <p>W/S to move paddle · First to 7 wins!</p>
          )}
        </div>
      </section>
    </div>
  );
}
